/**
 * useMapViewport hook.
 *
 * Manages viewport state (center, zoom, bearing, pitch) with debounced
 * callbacks for viewport-triggered queries.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import type { GeoCoord, MapViewport } from '../types/mapTypes';
import { RWANDA_CENTER, RWANDA_ZOOM, VIEWPORT_DEBOUNCE_MS } from '../config/mapboxConfig';

interface UseMapViewportOptions {
  initialCenter?: GeoCoord;
  initialZoom?: number;
  debounceMs?: number;
  onViewportChange?: (viewport: MapViewport) => void;
}

export function useMapViewport(options: UseMapViewportOptions = {}) {
  const {
    initialCenter = RWANDA_CENTER,
    initialZoom = RWANDA_ZOOM,
    debounceMs = VIEWPORT_DEBOUNCE_MS,
    onViewportChange,
  } = options;

  const [viewport, setViewport] = useState<MapViewport>({
    center: initialCenter,
    zoom: initialZoom,
    bearing: 0,
    pitch: 0,
  });

  const [navigateRequested, setNavigateRequested] = useState(false);
  const navigateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastCallbackRef = useRef<MapViewport | null>(null);

  // Debounced viewport change callback
  const handleViewportChange = useCallback(
    (newViewport: MapViewport) => {
      setViewport(newViewport);

      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        if (
          !lastCallbackRef.current ||
          lastCallbackRef.current.center[0] !== newViewport.center[0] ||
          lastCallbackRef.current.center[1] !== newViewport.center[1] ||
          lastCallbackRef.current.zoom !== newViewport.zoom
        ) {
          lastCallbackRef.current = newViewport;
          onViewportChange?.(newViewport);
        }
      }, debounceMs);
    },
    [debounceMs, onViewportChange],
  );

  /**
   * Request navigation to a specific center/zoom.
   * Sets navigateRequested=true briefly so the map component knows to fly.
   */
  const requestNavigate = useCallback(
    (center: GeoCoord, zoom: number) => {
      setViewport((prev) => ({
        ...prev,
        center,
        zoom,
      }));
      setNavigateRequested(true);
      if (navigateTimerRef.current) clearTimeout(navigateTimerRef.current);
      navigateTimerRef.current = setTimeout(() => setNavigateRequested(false), 200);
    },
    [],
  );

  /**
   * Fly to Rwanda default view.
   */
  const zoomToRwanda = useCallback(() => {
    requestNavigate(RWANDA_CENTER, RWANDA_ZOOM);
  }, [requestNavigate]);

  /**
   * Fit bounds around a set of coordinates.
   */
  const fitBounds = useCallback(
    (coords: GeoCoord[]) => {
      if (coords.length === 0) {
        zoomToRwanda();
        return;
      }

      let minLng = Infinity,
        maxLng = -Infinity,
        minLat = Infinity,
        maxLat = -Infinity;
      for (const [lng, lat] of coords) {
        if (lng < minLng) minLng = lng;
        if (lng > maxLng) maxLng = lng;
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
      }

      const center: GeoCoord = [(minLng + maxLng) / 2, (minLat + maxLat) / 2];
      const lngSpan = maxLng - minLng;
      const latSpan = maxLat - minLat;
      const span = Math.max(lngSpan, latSpan);

      // Calculate zoom from span
      let zoom = 18 - Math.log2(span * 111 + 1);
      zoom = Math.max(6, Math.min(20, zoom));

      requestNavigate(center, zoom);
    },
    [requestNavigate],
  );

  // Cleanup timers
  useEffect(() => {
    return () => {
      if (navigateTimerRef.current) clearTimeout(navigateTimerRef.current);
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  return {
    viewport,
    navigateRequested,
    handleViewportChange,
    requestNavigate,
    zoomToRwanda,
    fitBounds,
  };
}
